import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FilterBar, { buildQueryString } from "../components/dashboard/FilterBar";
import {
  NarrativePanel,
  PatternsPanel,
  WatchlistPanel,
  AnomaliesPanel,
} from "../components/insights/panels";
import CopilotEffectivenessTile from "../components/insights/CopilotEffectivenessTile";

const DEFAULT_FILTERS = {
  service_lines: [],
  issue_types: [],
  statuses: [],
  start_date: "",
  end_date: "",
  preset: "30d",
};

export default function Insights() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [issueTypes, setIssueTypes] = useState([]);

  const [patterns, setPatterns] = useState(null);
  const [watchlist, setWatchlist] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [narrative, setNarrative] = useState(null);

  const [regeneratingN, setRegeneratingN] = useState(false);

  const canRegenerate = user?.role === "hr" || user?.role === "compliance";
  const queryString = useMemo(() => buildQueryString(filters), [filters]);

  useEffect(() => {
    api
      .get("/config/issue-taxonomy")
      .then((r) => setIssueTypes(Object.keys(r.data?.taxonomy || {})))
      .catch(() => setIssueTypes([]));
  }, []);

  // Serialize filters to JSON body for POST endpoints
  const filtersBody = useMemo(() => {
    const out = {};
    if (filters.service_lines?.length) out.service_lines = filters.service_lines.join(",");
    if (filters.issue_types?.length) out.issue_types = filters.issue_types.join(",");
    if (filters.statuses?.length) out.statuses = filters.statuses.join(",");
    if (filters.start_date) out.start_date = filters.start_date;
    if (filters.end_date) out.end_date = filters.end_date;
    return out;
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    const qs = queryString ? `?${queryString}` : "";

    // Reset panels so skeletons flash
    setPatterns(null);
    setWatchlist(null);
    setAnomalies(null);
    setNarrative(null);

    const fetchers = [
      api.get(`/insights/patterns${qs}`).then((r) => !cancelled && setPatterns(r.data)),
      api.get(`/insights/watchlist${qs}`).then((r) => !cancelled && setWatchlist(r.data)),
      api.get(`/insights/anomalies${qs}`).then((r) => !cancelled && setAnomalies(r.data)),
      api
        .post("/insights/narrative", { filters: filtersBody })
        .then((r) => !cancelled && setNarrative(r.data)),
    ];
    Promise.all(fetchers).catch((err) => {
      if (!cancelled)
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const regenerateNarrative = async () => {
    setRegeneratingN(true);
    try {
      const { data } = await api.post("/insights/narrative", {
        filters: filtersBody,
        regenerate: true,
      });
      setNarrative(data);
      if (data?.llm_available === false) toast.error("AI unavailable — showed rule-based summary");
      else toast.success("Narrative regenerated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRegeneratingN(false);
    }
  };

  const drillToCases = (patch = {}) => {
    const params = new URLSearchParams();
    const svc = patch.service_lines ?? filters.service_lines;
    const iss = patch.issue_types ?? filters.issue_types;
    const sts = patch.statuses ?? filters.statuses;
    if (svc?.length) params.set("service_lines", svc.join(","));
    if (iss?.length) params.set("issue_types", iss.join(","));
    if (sts?.length) params.set("statuses", sts.join(","));
    if (patch.file_number) params.set("file_number", String(patch.file_number));
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);
    navigate(`/cases?${params.toString()}`);
  };

  return (
    <div data-testid="insights-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className="font-serif text-3xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            Insights
          </h1>
          <p
            className="mt-1 text-sm"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            Pattern detection, risk scoring, anomalies, and an AI-assisted executive narrative.
          </p>
        </div>
      </div>

      <FilterBar issueTypes={issueTypes} onFiltersChange={setFilters} />

      <div className="space-y-6">
        <NarrativePanel
          data={narrative}
          loading={!narrative}
          canRegenerate={canRegenerate}
          onRegenerate={regenerateNarrative}
          regenerating={regeneratingN}
        />

        {["hr", "compliance", "leadership"].includes(user?.role) && (
          <CopilotEffectivenessTile />
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <PatternsPanel data={patterns} loading={!patterns} onDrill={drillToCases} />
          </div>
          <div className="space-y-6">
            <AnomaliesPanel data={anomalies} loading={!anomalies} />
            <WatchlistPanel data={watchlist} loading={!watchlist} />
          </div>
        </div>
      </div>
    </div>
  );
}
